import React, { useEffect, useRef, useState } from "react";
import "./DraggableItem.css";

interface IDraggableItemProps extends React.HTMLAttributes<HTMLDivElement>{
    position: {x: number, y: number};
    onPositionChangeStart?: (position: {x: number, y: number})=>void;
    onPositionChange?: (position: {x: number, y: number})=>void;
    onPositionChangeEnd?: (position: {x: number, y: number})=>void;
    onBounding?: (rect: DOMRect)=>void;
}

export const DraggableItem = ({position, onBounding, onPositionChangeEnd, onPositionChangeStart, onPositionChange, children, className, ...htmlAttributes}: IDraggableItemProps)=>{
    const [internalPosition, setInternalPosition] = useState({x: 0, y: 0});
    const [isDragging, setIsDragging] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const DRAG_THRESHOLD = 10; // pixels
    
    useEffect(()=>{
        if (!position){
            setInternalPosition({x: 0, y: 0});
        } else
        if (internalPosition.x != position.x || internalPosition.y != position.y){
            setInternalPosition({...position});
        }
    }, [position, position?.x, position?.y]);

    useEffect(()=>{
        if (!ref.current){
            return;
        }
        onBounding?.(ref.current.getBoundingClientRect());
    }, [ref.current, position, internalPosition])

    return <div
        ref={ref}
        className={`DraggableItem ${className || ''}`}
        {...htmlAttributes}
        style={{
            top: (internalPosition?.y || 0) + 'px',
            left: (internalPosition?.x || 0) + 'px'
        }}
        onMouseDown={(downEvent)=>{
            // Only allow dragging with left mouse button
            if (downEvent.button !== 0) {
                return;
            }
            downEvent.stopPropagation();
            const lastSelect = window.document.body.style.userSelect;
            window.document.body.style.userSelect = 'none';
            
            const startX = downEvent.clientX;
            const startY = downEvent.clientY;
            let dragStarted = false;
            
            const moveHandler = (moveEvent: MouseEvent)=>{
                // Check if we've moved beyond the threshold
                const deltaX = moveEvent.clientX - startX;
                const deltaY = moveEvent.clientY - startY;
                const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                
                if (!dragStarted && distance > DRAG_THRESHOLD) {
                    dragStarted = true;
                    setIsDragging(true);
                    onPositionChangeStart?.(position);
                }
                
                // Always update position (for smooth movement), but only notify parent if drag started
                const nextPosition = {
                    x: internalPosition.x +=moveEvent.movementX,
                    y: internalPosition.y +=moveEvent.movementY
                }
                setInternalPosition(nextPosition);
                
                if (dragStarted && (moveEvent.clientX != downEvent.clientX || moveEvent.clientY != downEvent.clientY)){
                    onPositionChange?.(nextPosition);
                }
            }
            
            const upHandler = (moveEvent: MouseEvent)=>{
                window.removeEventListener('mousemove', moveHandler);
                window.removeEventListener('mouseup', upHandler);
                window.document.body.style.userSelect = lastSelect;
                
                if (dragStarted && (moveEvent.clientX != downEvent.clientX || moveEvent.clientY != downEvent.clientY)){
                    onPositionChangeEnd?.(internalPosition);          
                }
                setIsDragging(false);
            }
            
            window.addEventListener('mousemove', moveHandler);
            window.addEventListener('mouseup', upHandler);
        }}
    >
        {children}
    </div>
}